import React, { forwardRef, useEffect, useRef } from 'react'
import { FiImage, FiMic, FiPaperclip, FiSend, FiX } from 'react-icons/fi'

const MAX_HEIGHT = 132

const AssistantComposer = forwardRef(function AssistantComposer({
  value,
  onChange,
  disabled,
  placeholder,
  label,
  images,
  onFiles,
  onRemove,
  listening,
  onMic,
  dictateLabel,
  sendLabel,
  imageLabel,
  attachLabel,
  onDocuments,
}, ref) {
  const box = useRef(null)
  const field = useRef(null)
  const picker = useRef(null)
  const documents = useRef(null)

  useEffect(() => {
    const node = field.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${Math.min(node.scrollHeight, MAX_HEIGHT)}px`
  }, [value])

  const take = (list) => {
    const files = Array.from(list || []).filter((file) => file && String(file.type || '').startsWith('image/'))
    if (files.length) onFiles(files)
  }

  return (
    <div
      className="feature-search__composer"
      onDragOver={(event) => {
        event.preventDefault()
        box.current?.classList.add('is-dropping')
      }}
      onDragLeave={() => box.current?.classList.remove('is-dropping')}
      onDrop={(event) => {
        event.preventDefault()
        box.current?.classList.remove('is-dropping')
        take(event.dataTransfer && event.dataTransfer.files)
      }}
    >
      <div className="feature-search__composer-box" ref={box}>
        {images.length > 0 ? (
          <div className="feature-search__previews">
            {images.map((image) => (
              <div className="feature-search__preview" key={image.key}>
                {image.url ? <img src={image.url} alt="" /> : null}
                <button type="button" aria-label={image.removeLabel} onClick={() => onRemove(image.key)}>
                  <FiX aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <textarea
          ref={(node) => {
            field.current = node
            if (typeof ref === 'function') ref(node)
            else if (ref) ref.current = node
          }}
          className="feature-search__composer-input"
          rows={1}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={label}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') return
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault()
              const form = event.currentTarget.form
              if (!form) return
              if (typeof form.requestSubmit === 'function') form.requestSubmit()
              else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
            }
          }}
        />
        <div className="feature-search__composer-bar">
          <input
            ref={picker}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            hidden
            onChange={(event) => {
              take(event.target.files)
              event.target.value = ''
            }}
          />
          <input
            ref={documents}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,application/pdf"
            hidden
            onChange={(event) => {
              if (event.target.files && event.target.files.length && onDocuments) onDocuments()
              event.target.value = ''
            }}
          />
          <button type="button" aria-label={attachLabel} onClick={() => documents.current && documents.current.click()}>
            <FiPaperclip aria-hidden="true" />
          </button>
          <button type="button" aria-label={imageLabel} onClick={() => picker.current && picker.current.click()}>
            <FiImage aria-hidden="true" />
          </button>
          <button type="button" className={listening ? 'is-on' : ''} aria-pressed={listening} aria-label={dictateLabel} onClick={onMic}>
            <FiMic aria-hidden="true" />
          </button>
          <button type="submit" className="feature-search__submit" disabled={disabled} aria-label={sendLabel}>
            <FiSend aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  )
})

export default AssistantComposer
